import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";

const EMPTY_FORM = {
  title: "",
  position: "",
  image: null,
  description: "",
};

const StudentTestimonialFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const isEditMode = mode === "edit";
  const pageTitle = useMemo(() => (isEditMode ? "Edit Student Testimonial" : "Create Student Testimonial"), [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchItem = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/student-testimonials/${id}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Student testimonial not found");
          navigate("/student-testimonials");
          return;
        }

        setForm({
          title: item.title || "",
          position: item.position || "",
          image: null,
          description: item.description || "",
        });
        setImagePreviewUrl(item.image_url || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load student testimonial");
        navigate("/student-testimonials");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchItem();
  }, [id, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.title.trim()) nextErrors.title = "Title is required.";
    if (!form.position.trim()) nextErrors.position = "Position is required.";
    if (!form.description.trim()) nextErrors.description = "Description is required.";
    if (!isEditMode && !form.image) nextErrors.image = "Student image is required.";
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title.trim());
      payload.append("position", form.position.trim());
      payload.append("description", form.description.trim());
      if (form.image) payload.append("image", form.image);

      if (isEditMode) {
        await apiClient.put(`/student-testimonials/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Student testimonial updated successfully");
      } else {
        await apiClient.post("/student-testimonials", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Student testimonial created successfully");
      }

      navigate("/student-testimonials");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save student testimonial");
    } finally {
      setIsSaving(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading student testimonial editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/student-testimonials")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Student Testimonials
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input value={form.title} onChange={(event) => handleInputChange("title", event.target.value)} className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.title}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Position</label>
              <Input value={form.position} onChange={(event) => handleInputChange("position", event.target.value)} className={`rounded-lg ${formErrors.position ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.position}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <textarea value={form.description} onChange={(event) => handleInputChange("description", event.target.value)} rows={6} className={`min-h-[160px] w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-700 outline-none ${formErrors.description ? "border-red-300 focus:ring-2 focus:ring-red-500" : "border-slate-200 focus:ring-2 focus:ring-primary/30"}`} />
              <FieldError>{formErrors.description}</FieldError>
            </div>
          </div>

          <div className="space-y-2">
            <InlineFormFileUpload label="Student Image" value={form.image} onChange={(file) => handleInputChange("image", file)} existingFiles={imagePreviewUrl ? [imagePreviewUrl] : []} accept={IMAGE_TYPES} maxSizeBytes={5 * 1024 * 1024} required={!isEditMode} error={formErrors.image} helperText="JPG, PNG, GIF or WEBP — maximum 5 MB" />
            <ImageUploadGuidance requirementKey="studentTestimonial" file={form.image} src={imagePreviewUrl} />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/student-testimonials")}>
            Cancel
          </Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Student Testimonial" : "Create Student Testimonial"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StudentTestimonialFormPage;
